import React, { useMemo, useState } from "react"
import {
  BENCHMARK_SOURCE_URL,
  CALCULATOR_CURRENCIES,
  DEFAULT_AGENCY_FEE_PERCENTAGE,
  MAX_AGENCY_FEE_PERCENTAGE,
  MIN_AGENCY_FEE_PERCENTAGE,
  NUMBER_OF_WORKHOURS_PER_MONTH,
  TOTAL_RECRUITER_TIME_ON_MANUAL_TASKS,
  EMG_COST_NGN,
  calculateAgencySavings,
  calculateInHouseSavings,
  convertMoneyInput,
  emgCostForCurrency,
  formatMoney,
  formatPercentage,
} from "../utils/costSavingCalculator"

const TABS = [
  { id: "agency", label: "Agency Recruitment" },
  { id: "inhouse", label: "In-house Recruitment" },
]

const PLACEHOLDERS = {
  NGN: { salary: "e.g. 3600000", recruiter: "e.g. 300000" },
  USD: { salary: "e.g. 24000", recruiter: "e.g. 2000" },
  KES: { salary: "e.g. 313000", recruiter: "e.g. 26000" },
}

const ResultRow = ({ label, value, emphasize = false, tone = "default" }) => {
  const toneClass = {
    default: "text-gray-900",
    positive: "text-emerald-700",
    negative: "text-amber-800",
    muted: "text-gray-500",
  }[tone]

  return (
    <div className="flex items-start justify-between gap-4 border-b border-gray-100 py-3 last:border-b-0">
      <span className="text-sm text-gray-600">{label}</span>
      <span className={`text-right text-sm ${emphasize ? "text-base font-semibold" : "font-medium"} ${toneClass}`}>
        {value}
      </span>
    </div>
  )
}

const SavingOutputs = ({ result, extraRows = [], currency }) => {
  if (result.status === "empty") {
    return (
      <p className="text-sm text-gray-500">
        Enter a valid amount in the selected currency to see estimated savings. All figures are estimates, not guaranteed savings.
      </p>
    )
  }

  if (result.status === "invalid") {
    return (
      <p className="text-sm text-red-600">
        Please enter a valid non-negative number. Percentages cannot be calculated from invalid values.
      </p>
    )
  }

  const money = (value) => formatMoney(value, currency)
  const isAdditionalCost = result.costSaving < 0
  const savingValue = money(Math.abs(result.costSaving))
  const reductionValue = result.costReductionPercentage === null
    ? "—"
    : formatPercentage(result.costReductionPercentage)

  return (
    <div>
      {extraRows.map((row) => (
        <ResultRow key={row.label} label={row.label} value={row.value} />
      ))}
      <ResultRow label="EmployMeGlobal cost (estimate)" value={money(result.emgCost)} />
      <ResultRow
        label={isAdditionalCost ? "Estimated additional cost" : "Estimated cost saving"}
        value={savingValue}
        emphasize
        tone={isAdditionalCost ? "negative" : "positive"}
      />
      <ResultRow
        label="Estimated cost reduction"
        value={reductionValue}
        tone={result.costReductionPercentage === null ? "muted" : isAdditionalCost ? "negative" : "positive"}
      />
      {result.costReductionPercentage === null && (
        <p className="mt-3 text-xs text-gray-500">
          Cost reduction percentage is unavailable because the current estimated cost is {money(0)}.
        </p>
      )}
      {isAdditionalCost && (
        <p className="mt-3 text-xs text-amber-800">
          In this estimate, EmployMeGlobal would cost more than the current approach by {savingValue}.
        </p>
      )}
      <p className="mt-4 text-xs text-gray-500">
        These results are estimates only and are not guaranteed savings.
      </p>
    </div>
  )
}

const Assumptions = ({ children }) => (
  <details className="mt-6 rounded-lg border border-gray-200 bg-gray-50 px-4 py-3">
    <summary className="cursor-pointer text-sm font-semibold text-gray-900">
      How we calculate your savings
    </summary>
    <div className="mt-3 space-y-2 text-sm leading-relaxed text-gray-600">
      {children}
    </div>
  </details>
)

const CostSavingCalculator = () => {
  const [tab, setTab] = useState("agency")
  const [currency, setCurrency] = useState("NGN")
  const [annualSalary, setAnnualSalary] = useState("")
  const [agencyFeePercentage, setAgencyFeePercentage] = useState(DEFAULT_AGENCY_FEE_PERCENTAGE)
  const [recruiterSalary, setRecruiterSalary] = useState("")

  const emgCost = emgCostForCurrency(currency)
  const placeholders = PLACEHOLDERS[currency] || PLACEHOLDERS.NGN
  const currencyMeta = CALCULATOR_CURRENCIES.find((item) => item.id === currency) || CALCULATOR_CURRENCIES[0]
  const money = (value) => formatMoney(value, currency)

  const switchCurrency = (nextCurrency) => {
    if (nextCurrency === currency) return
    setAnnualSalary((prev) => convertMoneyInput(prev, currency, nextCurrency))
    setRecruiterSalary((prev) => convertMoneyInput(prev, currency, nextCurrency))
    setCurrency(nextCurrency)
  }

  const agencyResult = useMemo(
    () => calculateAgencySavings(annualSalary, agencyFeePercentage, emgCost),
    [annualSalary, agencyFeePercentage, emgCost]
  )
  const inHouseResult = useMemo(
    () => calculateInHouseSavings(recruiterSalary, emgCost),
    [recruiterSalary, emgCost]
  )

  return (
    <section id="cost-saving-calculator" className="scroll-mt-24 border-t border-gray-200 bg-white py-16 sm:py-20">
      <div className="mx-auto max-w-7xl px-6 sm:px-10 lg:px-12">
        <div className="mx-auto max-w-3xl text-center">
          <h2 className="text-3xl font-bold text-gray-900 sm:text-4xl">
            EmployMeGlobal Cost Saving Calculator
          </h2>
          <p className="mt-4 text-lg text-gray-600">
            Estimate how much your business could save by using EmployMeGlobal instead of agency or in-house recruitment.
            Results are estimates, not guaranteed savings.
          </p>
        </div>

        <div className="mx-auto mt-10 max-w-3xl overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm">
          <div className="flex flex-col gap-3 border-b border-gray-200 bg-gray-50 px-4 py-3 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">
                Job post price & currency
              </p>
              <p className="mt-0.5 text-sm font-semibold text-gray-900">
                {money(emgCost)} per vacancy
              </p>
            </div>
            <div className="flex rounded-lg border border-gray-200 bg-white p-1" role="group" aria-label="Calculator currency">
              {CALCULATOR_CURRENCIES.map((item) => {
                const selected = currency === item.id
                return (
                  <button
                    key={item.id}
                    type="button"
                    aria-pressed={selected}
                    title={item.hint}
                    onClick={() => switchCurrency(item.id)}
                    className={`min-w-[5.75rem] rounded-md px-3 py-1.5 text-sm font-semibold transition-colors ${
                      selected
                        ? "bg-[#00A5E6] text-white shadow-sm"
                        : "text-gray-600 hover:text-gray-900"
                    }`}
                  >
                    {item.shortLabel || item.label}
                  </button>
                )
              })}
            </div>
          </div>
          <p className="border-b border-gray-100 bg-white px-4 py-2 text-xs text-gray-500">
            Default job posting price is {formatMoney(EMG_COST_NGN, "NGN")}. Switching currency converts the posting price and any amounts you already typed using estimated exchange rates.
            Enter salaries in {currencyMeta.hint}.
            {currency === "KES" ? " Kenyan checkout supports M-PESA." : ""}
          </p>

          <div className="flex border-b border-gray-200 bg-gray-50 p-1" role="tablist" aria-label="Recruitment method">
            {TABS.map((item) => {
              const selected = tab === item.id
              return (
                <button
                  key={item.id}
                  type="button"
                  role="tab"
                  aria-selected={selected}
                  onClick={() => setTab(item.id)}
                  className={`flex-1 rounded-lg px-4 py-2.5 text-sm font-medium transition-colors ${
                    selected
                      ? "bg-white text-gray-900 shadow-sm"
                      : "text-gray-500 hover:text-gray-800"
                  }`}
                >
                  {item.label}
                </button>
              )
            })}
          </div>

          <div className="p-6 sm:p-8">
            {tab === "agency" && (
              <div>
                <div className="grid gap-6 sm:grid-cols-2">
                  <label className="block">
                    <span className="text-sm font-medium text-gray-900">
                      Annual salary of vacancy ({currency})
                    </span>
                    <input
                      type="text"
                      inputMode="decimal"
                      value={annualSalary}
                      onChange={(e) => setAnnualSalary(e.target.value)}
                      placeholder={placeholders.salary}
                      className="mt-2 w-full rounded-lg border border-gray-300 px-3 py-2.5 text-sm text-gray-900 outline-none transition-colors focus:border-[#00A5E6] focus:ring-2 focus:ring-[#00A5E6]/20"
                    />
                  </label>
                  <label className="block">
                    <span className="text-sm font-medium text-gray-900">
                      Agency fee ({agencyFeePercentage}%)
                    </span>
                    <input
                      type="range"
                      min={MIN_AGENCY_FEE_PERCENTAGE}
                      max={MAX_AGENCY_FEE_PERCENTAGE}
                      step={1}
                      value={agencyFeePercentage}
                      onChange={(e) => setAgencyFeePercentage(Number(e.target.value))}
                      className="mt-4 w-full accent-[#00A5E6]"
                    />
                    <div className="mt-1 flex justify-between text-xs text-gray-500">
                      <span>{MIN_AGENCY_FEE_PERCENTAGE}%</span>
                      <span>{MAX_AGENCY_FEE_PERCENTAGE}%</span>
                    </div>
                  </label>
                </div>

                <div className="mt-8 rounded-lg border border-gray-100 bg-gray-50 px-4 py-2">
                  <SavingOutputs
                    result={agencyResult}
                    currency={currency}
                    extraRows={
                      agencyResult.status === "ok"
                        ? [{ label: "Estimated agency fee", value: money(agencyResult.agencyFee) }]
                        : []
                    }
                  />
                </div>

                <Assumptions>
                  <p>Agency fees are estimated at 15–25% of the successful candidate’s annual base salary. The default is 20%.</p>
                  <p>EmployMeGlobal costs {money(emgCost)} per vacancy and is treated as replacing the agency for that vacancy.</p>
                  <p>Additional agency services and other recruitment expenses are excluded.</p>
                  <p>Estimated saving = agency fee − EmployMeGlobal cost.</p>
                </Assumptions>
              </div>
            )}

            {tab === "inhouse" && (
              <div>
                <label className="block max-w-md">
                  <span className="text-sm font-medium text-gray-900">
                    Monthly salary of HR officer or recruiter ({currency})
                  </span>
                  <input
                    type="text"
                    inputMode="decimal"
                    value={recruiterSalary}
                    onChange={(e) => setRecruiterSalary(e.target.value)}
                    placeholder={placeholders.recruiter}
                    className="mt-2 w-full rounded-lg border border-gray-300 px-3 py-2.5 text-sm text-gray-900 outline-none transition-colors focus:border-[#00A5E6] focus:ring-2 focus:ring-[#00A5E6]/20"
                  />
                </label>

                <div className="mt-8 rounded-lg border border-gray-100 bg-gray-50 px-4 py-2">
                  <SavingOutputs
                    result={inHouseResult}
                    currency={currency}
                    extraRows={
                      inHouseResult.status === "ok"
                        ? [
                            {
                              label: "Estimated recruiter time spent per vacancy",
                              value: `${TOTAL_RECRUITER_TIME_ON_MANUAL_TASKS} hours`,
                            },
                            {
                              label: "Estimated recruiter time cost per vacancy",
                              value: money(inHouseResult.recruiterTimeCost),
                            },
                          ]
                        : []
                    }
                  />
                </div>

                <Assumptions>
                  <p>
                    Recruiters spend an estimated {TOTAL_RECRUITER_TIME_ON_MANUAL_TASKS} hours on manual administrative tasks per vacancy, including CV screening.
                    This benchmark is based on UK recruitment research reported by Totaljobs and summarised by FormaCV.
                  </p>
                  <p>
                    <a
                      href={BENCHMARK_SOURCE_URL}
                      target="_blank"
                      rel="noreferrer"
                      className="font-medium text-[#00A5E6] hover:text-[#008FCC] hover:underline"
                    >
                      View the recruiter productivity benchmark source
                    </a>
                  </p>
                  <p>
                    Monthly working hours are calculated as 21 days × 8 hours = {NUMBER_OF_WORKHOURS_PER_MONTH} hours.
                    Recruiter hourly cost = monthly salary ÷ {NUMBER_OF_WORKHOURS_PER_MONTH}.
                  </p>
                  <p>EmployMeGlobal costs {money(emgCost)} per vacancy. The calculation assumes all {TOTAL_RECRUITER_TIME_ON_MANUAL_TASKS} hours are saved.</p>
                  <p>
                    The value of time saved is not necessarily a direct cash saving, because the recruiter’s salary may remain unchanged.
                  </p>
                </Assumptions>
              </div>
            )}
          </div>
        </div>
      </div>
    </section>
  )
}

export default CostSavingCalculator
